import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useLang } from "../Utility/Languagecontext";
import { DEV_DETAILS, pick } from "../Data/Developersdata";

// ============ فيديو الخلفية ============
import devsVideo from "../Assets/palm-web.mp4";
const DEVS_POSTER = null;

const EASE = [0.76, 0, 0.24, 1];

const MaskLine = ({ children, delay = 0, show }) => (
  <span className="mask">
    <motion.span
      className="mask__inner"
      initial={false}
      animate={{ y: show ? 0 : "115%" }}
      transition={{ duration: 1.1, delay: show ? delay : 0, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

// إضاءة هادية بتتبع الماوس جوه الخانة
const spotlight = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
};

const Arrow = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
    <path d="M7 17L17 7M9 7h8v8" />
  </svg>
);

export const MainOurDevelopers = () => {
  const { t, lang, locale } = useLang();
  const d = t.developers;
  const num = (v, min = 1) =>
    Number.isFinite(v) ? v.toLocaleString(locale, { minimumIntegerDigits: min, useGrouping: false }) : null;
  const projectsLabel = (n) => (typeof d.projects === "function" ? d.projects(n) : d.projects);

  const headRef = useRef(null);
  const headIn = useInView(headRef, { amount: 0.3 });

  // ---- الفيديو: بيتحمّل بس لما القسم يقرّب، وبيشتغل وهو ظاهر بس ----
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const reduce = useReducedMotion();
  const visible = useInView(sectionRef, { amount: 0 });
  const near = useInView(sectionRef, { once: true, margin: "600px 0px 600px 0px" });
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start end", "end start"] });
  const vy = useTransform(scrollYProgress, [0, 1], ["-7%", "7%"]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !near) return;
    v.muted = true;
    v.defaultMuted = true;
    v.playsInline = true;

    const tryPlay = () => {
      if (!visible || reduce) return;
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    };

    if (visible && !reduce) {
      tryPlay();
      v.addEventListener("loadeddata", tryPlay);
      v.addEventListener("canplay", tryPlay);
      window.addEventListener("pointerdown", tryPlay, { once: true });
      window.addEventListener("touchstart", tryPlay, { once: true });
    } else {
      v.pause();
    }
    return () => {
      v.removeEventListener("loadeddata", tryPlay);
      v.removeEventListener("canplay", tryPlay);
      window.removeEventListener("pointerdown", tryPlay);
      window.removeEventListener("touchstart", tryPlay);
    };
  }, [visible, reduce, near]);

  return (
    <section className="devs" id="developers" ref={sectionRef}>
      {/* ====== خلفية الفيديو ====== */}
      <div className="devs__bg" aria-hidden="true">
        <motion.div className="devs__bg-move" style={reduce ? undefined : { y: vy }}>
          <video
            ref={videoRef}
            className="devs__video"
            src={near && !reduce ? devsVideo : undefined}
            poster={DEVS_POSTER || undefined}
            muted
            loop
            playsInline
            preload="none"
            disablePictureInPicture
            tabIndex={-1}
          />
        </motion.div>
        <div className="devs__bg-shade" />
      </div>

      <div className="devs__head" ref={headRef}>
        <div>
          <p className="devs__label">{d.label}</p>
          <h2 className="devs__title">
            {d.title.map((line, i) => (
              <MaskLine key={line} delay={i * 0.14} show={headIn}>
                {line}
              </MaskLine>
            ))}
          </h2>
        </div>
        <p className="devs__sub">{d.sub}</p>
      </div>

      <ul className="devs__wall">
        {/* كل مطوّر: اسمه ولوجوه وصفحته من نفس العنصر في DEV_DETAILS (مفيش لخبطة في الترتيب) */}
        {DEV_DETAILS.map((dev, i) => {
          const name = pick(dev.name, lang);
          return (
            <motion.li
              key={dev.slug}
              className="dev"
              onMouseMove={spotlight}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1, delay: 0.08 * (i % 4), ease: EASE }}
            >
              <Link to={`/developers/${dev.slug}`} className="dev__hit" aria-label={`${t.devPage.view}: ${name}`} />

              <span className="dev__no">{num(i + 1, 2)}</span>
              <span className="dev__go" aria-hidden="true">
                <Arrow />
              </span>

              <span className="dev__stage">
                <i className="dev__arch" aria-hidden="true" />
                {dev.logo ? (
                  <img
                    className="dev__logo"
                    data-dev={dev.slug}
                    src={dev.logo}
                    alt={name}
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <span className="dev__mark">{pick(dev.mark, lang)}</span>
                )}
              </span>

              <span className="dev__foot">
                <span className="dev__name">{name}</span>
                <span className="dev__meta">
                  {[
                    num(dev.projects) && `${num(dev.projects)} ${projectsLabel(dev.projects)}`,
                    num(dev.since) && `${d.since} ${num(dev.since)}`,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </span>
            </motion.li>
          );
        })}
      </ul>

      <div className="devs__note">
        <p>{d.note}</p>
        <a href="#contact" className="btn btn--ghost">
          {d.cta}
        </a>
      </div>
    </section>
  );
};
